import React from "react";
import { Card, CardContent, Typography, Button, Box } from "@material-ui/core";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("ErrorBoundary caught error:", error, errorInfo);
    
    this.setState((prevState) => ({
      error,
      errorInfo,
      errorCount: prevState.errorCount + 1,
    }));
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const isNetworkError =
        this.state.error?.message?.includes("Network") ||
        this.state.error?.message?.includes("ECONNREFUSED");

      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
            padding: "24px",
            background:
              "radial-gradient(circle at center, rgba(255,255,255,0.95) 0%, rgba(244,248,247,0.9) 100%)",
          }}
        >
          <Card
            sx={{
              maxWidth: 500,
              width: "100%",
              boxShadow:
                "0 22px 60px rgba(14, 72, 69, 0.10), 0 3px 10px rgba(15, 126, 119, 0.05)",
              border: "1px solid rgba(15, 126, 119, 0.09)",
            }}
          >
            <CardContent sx={{ padding: "40px 32px" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                <ErrorOutlineIcon
                  sx={{ fontSize: 40, color: "#d32f2f" }}
                />
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 800,
                    color: "#0F7E77",
                    letterSpacing: "0.01em",
                  }}
                >
                  {isNetworkError
                    ? "Connection Error"
                    : "Something Went Wrong"}
                </Typography>
              </Box>

              <Typography
                sx={{
                  color: "#6a7675",
                  mb: 2,
                  lineHeight: 1.6,
                  fontSize: "14px",
                }}
              >
                {isNetworkError
                  ? "We encountered a network connectivity issue. This could be due to internet connectivity or the server being temporarily unavailable."
                  : "An unexpected error occurred. Please try refreshing the page or contact support if the problem persists."}
              </Typography>

              {process.env.NODE_ENV === "development" && (
                <Box
                  sx={{
                    background: "#f5f5f5",
                    border: "1px solid #e0e0e0",
                    borderRadius: "4px",
                    padding: "12px",
                    mb: 2,
                    maxHeight: "200px",
                    overflowY: "auto",
                    fontFamily: "monospace",
                    fontSize: "11px",
                    color: "#666",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, display: "block", mb: 1 }}
                  >
                    Error Details:
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ display: "block", wordBreak: "break-word" }}
                  >
                    {this.state.error?.toString()}
                  </Typography>
                  {this.state.errorInfo && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        mt: 1,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {this.state.errorInfo.componentStack}
                    </Typography>
                  )}
                </Box>
              )}

              <Box sx={{ display: "flex", gap: 1, pt: 1 }}>
                <Button
                  variant="contained"
                  sx={{
                    background: "#0F7E77",
                    color: "white",
                    flex: 1,
                    textTransform: "none",
                    fontWeight: 600,
                    "&:hover": {
                      background: "#0d6963",
                    },
                  }}
                  onClick={this.handleReset}
                >
                  Try Again
                </Button>
                <Button
                  variant="outlined"
                  sx={{
                    borderColor: "#0F7E77",
                    color: "#0F7E77",
                    flex: 1,
                    textTransform: "none",
                    fontWeight: 600,
                    "&:hover": {
                      background: "rgba(15, 126, 119, 0.05)",
                      borderColor: "#0d6963",
                    },
                  }}
                  onClick={this.handleReload}
                >
                  Reload Page
                </Button>
              </Box>

              {this.state.errorCount > 3 && (
                <Typography
                  sx={{
                    mt: 2,
                    fontSize: "12px",
                    color: "#d32f2f",
                    textAlign: "center",
                  }}
                >
                  Multiple errors detected. Please clear your browser cache or contact support.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
